import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Award, Check, X } from 'lucide-react';
import { Alert, Button, Card, CardBody, CardHeader, CardTitle, EmptyState, Spinner } from '@pnl/ui';
import type { PendingFundedApproval } from '@pnl/sdk';
import { PnlApiError } from '@pnl/sdk';
import { api } from '../lib/api';

/**
 * Metas atingidas aguardando VALIDAÇÃO MANUAL (pedido do cliente, ago/2026).
 *
 * Bater a meta não promove mais a conta sozinha. Ela entra aqui com os números
 * que precisam ser conferidos — consistência, dias operados e drawdown usado —
 * e o time decide. Aprovar transforma a MESMA conta em funded (não cria nova).
 */
function money(cents: number, market: string): string {
  const usd = market === 'FOREX';
  return (cents / 100).toLocaleString(usd ? 'en-US' : 'pt-BR', {
    style: 'currency',
    currency: usd ? 'USD' : 'BRL',
  });
}

export function FundedApprovalsPage() {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'funded-approvals'],
    queryFn: () => api.listPendingFundedApprovals(),
  });

  function done(tone: 'success' | 'danger', text: string) {
    setMsg({ tone, text });
    void qc.invalidateQueries({ queryKey: ['admin', 'funded-approvals'] });
    setTimeout(() => setMsg(null), 5000);
  }

  const approve = useMutation({
    mutationFn: (id: string) => api.approveFundedPromotion(id),
    onSuccess: () => done('success', 'Conta promovida para Funded.'),
    onError: (e) =>
      done('danger', e instanceof PnlApiError ? e.api.message : 'Falha ao aprovar a conta'),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.rejectFundedPromotion(id, reason),
    onSuccess: () => done('success', 'Validação recusada — a conta segue em challenge.'),
    onError: (e) => done('danger', e instanceof PnlApiError ? e.api.message : 'Falha ao recusar'),
  });

  const items = query.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">Operação</p>
        <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
          Metas atingidas
        </h1>
        <p className="text-ink-500 mt-1 text-sm">
          Traders que bateram a meta do challenge e aguardam a nossa validação. A conta{' '}
          <strong>não vira funded sozinha</strong> — confira as regras abaixo e aprove.
        </p>
      </div>

      {msg && (
        <Alert tone={msg.tone} title={msg.tone === 'success' ? 'Feito' : 'Erro'}>
          {msg.text}
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Aguardando validação ({items.length})</CardTitle>
        </CardHeader>
        <CardBody>
          {query.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Award size={28} />}
              title="Nenhuma meta aguardando validação"
              description="Quando um trader bate a meta do challenge, a conta aparece aqui para análise."
            />
          ) : (
            <div className="divide-ink-100 divide-y">
              {items.map((it) => (
                <ApprovalRow
                  key={it.accountId}
                  item={it}
                  busy={approve.isPending || reject.isPending}
                  onApprove={() => approve.mutate(it.accountId)}
                  onReject={(reason) => reject.mutate({ id: it.accountId, reason })}
                />
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function ApprovalRow({
  item,
  busy,
  onApprove,
  onReject,
}: {
  item: PendingFundedApproval;
  busy: boolean;
  onApprove: () => void;
  onReject: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  return (
    <div className="py-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-ink-900 text-sm font-semibold">
            {item.traderFullName || item.traderEmail}{' '}
            <span className="text-ink-500 font-normal">· {item.traderEmail}</span>
          </p>
          <p className="text-ink-500 mt-0.5 text-xs">
            {item.label} · {item.planName} ({item.marketCode}) · meta batida em{' '}
            {new Date(item.metaReachedAt).toLocaleString('pt-BR')}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" onClick={onApprove} disabled={busy}>
            <Check size={13} /> Aprovar como Funded
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setRejecting((v) => !v)} disabled={busy}>
            <X size={13} /> Recusar
          </Button>
        </div>
      </div>

      {/* Números que precisam ser conferidos antes de promover. */}
      <div className="mt-3 grid gap-3 sm:grid-cols-4">
        <Metric label="Lucro" value={money(item.profitCents, item.marketCode)} />
        <Metric label="Meta" value={money(item.metaCents, item.marketCode)} />
        <Metric
          label={`Consistência (máx 50%)`}
          value={`${item.consistencyPct.toFixed(2)}%`}
          tone={item.consistencyOk ? 'ok' : 'bad'}
        />
        <Metric
          label="Drawdown usado"
          value={`${money(item.drawdownUsedCents, item.marketCode)} de ${money(item.drawdownLimitCents, item.marketCode)}`}
        />
      </div>
      <p className="text-ink-500 mt-2 text-xs">
        {item.tradedDays} dia(s) operado(s)
        {!item.consistencyOk && (
          <span className="text-danger-600 font-semibold">
            {' '}
            · melhor dia acima do limite de consistência
          </span>
        )}
      </p>

      {rejecting && (
        <div className="border-ink-200 bg-ink-50/60 mt-3 rounded-lg border p-3">
          <label className="text-ink-700 text-xs font-medium">
            Motivo (fica registrado e aparece pro trader)
          </label>
          <div className="mt-2 flex gap-2">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: melhor dia acima de 50% do lucro total"
              className="border-ink-200 focus:border-brand-500 focus:ring-brand-500/30 flex-1 rounded-lg bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2"
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                onReject(reason);
                setRejecting(false);
                setReason('');
              }}
              disabled={busy}
            >
              Confirmar recusa
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: 'ok' | 'bad' }) {
  return (
    <div className="border-ink-100 rounded-lg border px-3 py-2">
      <p className="text-2xs text-ink-500 uppercase tracking-wider">{label}</p>
      <p
        className={
          'mt-0.5 text-sm font-semibold tabular-nums ' +
          (tone === 'bad' ? 'text-danger-600' : tone === 'ok' ? 'text-success-600' : 'text-ink-900')
        }
      >
        {value}
      </p>
    </div>
  );
}
